<script lang="ts" setup>
import { ref, onUnmounted, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { downloadFile } from '@/app/core/services/files'
import { getErrorCode } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import {
  DBtn,
  DTextField,
  DTable,
  DLoading,
  XIcon,
  CheckIcon,
  Loading02Icon,
  FileCheck02Icon
} from 'front-library'
import {
  getImportRequests,
  ImportRequest
} from '@/app/core/services/import-requests'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)

/* Datos */
const search = ref()
const tableImportRequests = ref(null)
const refresh = ref()
const isRefreshData = ref<boolean>(false)

/* Datos componente table */
const columns = ref([
  { name: 'imports', title: t('import.list.importType'), table: 'tables.name' },
  {
    name: 'actions',
    title: t('import.list.actionType'),
    table: 'import_actions.name'
  },
  {
    name: 'status',
    title: t('import.list.status'),
    table: 'process_status.name'
  },
  { name: 'summary', title: t('import.list.summary') },
  { name: 'fileUrl', title: t('import.list.file') },
  {
    name: 'observation',
    title: t('import.list.observations'),
    table: 'import_requests.observation'
  },
  {
    name: 'fileErrorUrl',
    title: t('import.list.errorFile')
  },
  {
    name: 'created_at',
    title: t('import.list.creationDate'),
    table: 'import_requests.created_at'
  },
  {
    name: 'updated_at',
    title: t('import.list.lastUpdate'),
    table: 'import_requests.updated_at'
  }
])
const rows = ref<ImportRequest[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')
const percentage = (successRows: number, totalRows: number) => {
  if (Number.isNaN(successRows / totalRows)) return 0
  const valuePercentage = (successRows / totalRows) * 100
  return Math.trunc(valuePercentage)
}

/* Redirect's */
const createImportRequest = () => {
  void router.push({ name: 'CreateImportRequest' })
}

/* Listar importaciones */
const imports = async (load: boolean = true) => {
  if (load) loading.value = true
  isRefreshData.value = true
  try {
    const data: any = await getImportRequests(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
    isRefreshData.value = false
  } catch (error: any) {
    if (error.response?.data) {
      const code = getErrorCode(error.response.data?.code)
      notifications.addNotification({
        title: t('error.titles.' + code),
        text: t('error.messages.' + code),
        type: 'error',
        time: 10000
      })
    } else {
      notifications.addNotification({
        title: 'Error',
        text: error,
        type: 'error',
        time: 10000
      })
    }
    loading.value = false
    isRefreshData.value = false
  }
}

const download = async (urlFile: string) => {
  const nameFile = urlFile.split('.com/')[1]
  const urlDownload: any = await downloadFile(nameFile)
  const link = document.createElement('a')
  document.body.appendChild(link)
  link.href = urlDownload.url
  link.click()
}

onMounted(() => {
  refresh.value = setInterval(() => {
    void imports(false)
  }, 10000)
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('import.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('import.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="imports" />
          <DBtn @click.prevent="imports" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createImportRequest()"
          v-if="store.hasPermission('import:create')">
          {{
            t('form.button.create', {
              form: t('import.list.title', 0)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableImportRequests"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), imports()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), imports()">
      <template #status="data: any">
        <div class="flex items-center justify-between">
          <div>{{ data.statusProcess }}</div>
          <div v-if="data.statusProcess === 'Error'">
            <XIcon class="text-red-500" />
          </div>
          <div v-if="data.statusProcess === 'Completado'">
            <CheckIcon class="text-green-500" />
          </div>
          <div
            class="flex flex-col items-center"
            v-if="data.statusProcess === 'Pendiente'">
            <Loading02Icon class="rotate text-blue-600" />
            <span class="font-black text-xs tracking-wider text-blue-500">
              {{ `${data.percentage}%` }}</span
            >
          </div>
        </div>
      </template>
      <template #fileUrl="data: any">
        <div v-if="data.url != '' && data.url != null">
          <a @click="download(data.url)">
            <file-check-02-icon class="cursor-pointer"> </file-check-02-icon>
          </a>
        </div>
      </template>
      <template #summary="data: any">
        <div class="text-end">
          <p class="text-base">
            {{ `${data.success_rows ?? '0'}/${data.total_rows ?? '-'} ` }}
            <span class="text-sm font-semibold text-blue-700">{{
              `(${percentage(data.success_rows, data.total_rows)}%)`
            }}</span>
          </p>
        </div>
      </template>
      <template #fileErrorUrl="data: any">
        <div v-if="data.errorUrl != '' && data.errorUrl != null">
          <a @click="download(data.errorUrl)">
            <FileCheck02Icon class="cursor-pointer" />
          </a>
        </div>
        <div v-else>
          <div class="flex flex-col" v-if="data.statusProcess === 'Error'">
            <Loading02Icon class="rotate text-black" />
            <span
              class="font-black text-xs tracking-wider text-gray-400 text-wrap ...">
              {{ `${t('import.list.messageFileError')}` }}</span
            >
          </div>
        </div>
      </template>
      <template #refresh v-if="isRefreshData">
        <Loading02Icon class="rotate text-gray-400 mr-2" />
        <h1 class="text-gray-400 text-base" v-html="t('import.list.loading')" />
      </template>
    </DTable>
  </div>
</template>
